/**
 * Public booking widget translation strings.
 *
 * Detection priority:
 *   1. `?lang=de|en|tr` in the URL (explicit user choice)
 *   2. navigator.language fallback
 *   3. Default 'de' — primary market
 *
 * Kept in a single TS module (not a JSON loader) so the strings are
 * bundled with the widget chunk and the visitor never sees a flash of
 * untranslated text.
 */

export type WidgetLocale = 'de' | 'en' | 'tr' | 'fr' | 'it'
export const WIDGET_LOCALES: WidgetLocale[] = ['de', 'en', 'fr', 'it', 'tr']
export const LOCALE_LABELS: Record<WidgetLocale, string> = { de: 'DE', en: 'EN', tr: 'TR', fr: 'FR', it: 'IT' }
export const LOCALE_NAMES: Record<WidgetLocale, string> = { de: 'Deutsch', en: 'English', tr: 'Türkçe', fr: 'Français', it: 'Italiano' }

export interface WidgetStrings {
  // Step labels
  stepDateTime: string
  stepDetails: string
  stepConfirm: string
  // Date strip
  prevDay: string
  nextDay: string
  today: string
  tomorrow: string
  // Guests
  guests: string
  guestsSingular: string
  // Time picker
  pickTime: string
  noSlots: string
  closedToday: string
  notConfigured: string
  // Form
  firstName: string
  lastName: string
  email: string
  phone: string
  notes: string
  notesHint: string
  required: string
  // Buttons
  continue: string
  back: string
  submit: string
  submitting: string
  // Confirmation
  confirmedTitle: string
  pendingTitle: string
  reservedFor: string
  minutes: string
  until: string
  pendingHint: string
  // Errors
  errInvalidEmail: string
  errInvalidPhone: string
  errPaused: string
  // Marketing
  marketingConsent: string
  // Privacy (DSGVO) — required consent. {link} marks the Datenschutz link.
  privacyConsent: string
  privacyLink: string
  privacyRequired: string
  // Waitlist
  waitlistSwitch: string
  waitlistHelp: string
  // Header
  onlineReservation: string
  step: string
  poweredBy: string
  // Form extras
  notesPlaceholder: string
  phonePlaceholder: string
  // Availability strip (Day 22)
  availFull: string
  availTight: string
  availFree: string
  seatsFree: string          // "{n} frei"
  seatsTight: string         // "nur {n} frei"
  seatsFull: string          // "Voll"
  notAvailable: string       // "nicht verfügbar"
  capacityMeta: string       // "{cap} Plätze · {slots} Zeiten"
  // Paused / closed states
  pausedTitle: string
  pausedBody: string
  adminHint: string
  // Confirmation extras
  pendingReviewHint: string
  confirmSentTo: string      // "Eine Bestätigung wurde an {email} gesendet."
  addToCalendar: string
  afterVisit: string
  leaveReview: string
  submitFailed: string
  // Returning-guest recognition (Day 46)
  welcomeBack: string
  welcomeBackHint: string
  // Popular-time badge (Day 51)
  popular: string
}

const de: WidgetStrings = {
  stepDateTime: 'Datum & Zeit',
  stepDetails: 'Ihre Daten',
  stepConfirm: 'Bestätigung',
  prevDay: 'Vorheriger Tag',
  nextDay: 'Nächster Tag',
  today: 'Heute',
  tomorrow: 'Morgen',
  guests: 'Personen',
  guestsSingular: 'Person',
  pickTime: 'Uhrzeit wählen',
  noSlots: 'Heute keine freien Zeiten.',
  closedToday: 'An diesem Tag geschlossen.',
  notConfigured: 'Öffnungszeiten noch nicht hinterlegt.',
  firstName: 'Vorname',
  lastName: 'Nachname',
  email: 'E-Mail',
  phone: 'Telefon',
  notes: 'Anmerkung',
  notesHint: 'z. B. Allergien, Hochstuhl, Geburtstag',
  required: 'Pflichtfeld',
  continue: 'Weiter',
  back: 'Zurück',
  submit: 'Reservieren',
  submitting: 'Wird gesendet…',
  confirmedTitle: 'Reservierung bestätigt!',
  pendingTitle: 'Anfrage gesendet!',
  reservedFor: 'Reserviert für',
  minutes: 'Minuten',
  until: 'bis',
  pendingHint: 'Wir bestätigen Ihre Anfrage so schnell wie möglich.',
  errInvalidEmail: 'Bitte eine gültige E-Mail-Adresse eingeben.',
  errInvalidPhone: 'Bitte eine gültige Telefonnummer eingeben.',
  errPaused: 'Aktuell nehmen wir keine Reservierungen entgegen.',
  marketingConsent: 'Ich möchte gelegentlich Restaurant-News per E-Mail erhalten.',
  privacyConsent: 'Mit dem Absenden der Reservierung willige ich ein, dass meine angegebenen Daten zur Bearbeitung der Reservierung gespeichert und verarbeitet werden ({link}).',
  privacyLink: 'Datenschutzerklärung',
  privacyRequired: 'Bitte stimmen Sie der Verarbeitung Ihrer Daten zu, um zu reservieren.',
  waitlistSwitch: 'Auf die Warteliste setzen',
  waitlistHelp: 'Sie werden benachrichtigt, sobald ein Tisch frei wird.',
  onlineReservation: 'Online-Reservierung',
  step: 'Schritt',
  poweredBy: 'Powered by',
  notesPlaceholder: 'Geburtstag, Allergie, besondere Wünsche…',
  phonePlaceholder: '+49 30 …',
  availFull: 'Voll ausgebucht',
  availTight: 'Viele Slots sind knapp',
  availFree: 'Viele Slots verfügbar',
  seatsFree: '{n} frei',
  seatsTight: 'nur {n} frei',
  seatsFull: 'Voll',
  notAvailable: 'nicht verfügbar',
  capacityMeta: '{cap} Plätze · {slots} Zeiten',
  pausedTitle: 'Aktuell keine Reservierungen möglich',
  pausedBody: 'Wir nehmen vorübergehend keine Online-Reservierungen an. Bitte rufen Sie uns direkt an oder versuchen Sie es später noch einmal.',
  adminHint: 'Bitte loggen Sie sich ins Admin-Panel ein und tragen Sie die Öffnungszeiten ein.',
  pendingReviewHint: 'Wir werden Ihre Anfrage prüfen und uns bei Ihnen melden.',
  confirmSentTo: 'Eine Bestätigung wurde an {email} gesendet.',
  addToCalendar: 'Zu Kalender hinzufügen',
  afterVisit: 'Nach Ihrem Besuch:',
  leaveReview: 'Bewertung abgeben →',
  submitFailed: 'Reservierung fehlgeschlagen',
  welcomeBack: 'Willkommen zurück! 👋',
  welcomeBackHint: 'Schön, dass Sie wieder reservieren.',
  popular: 'Beliebt',
}

const en: WidgetStrings = {
  stepDateTime: 'Date & Time',
  stepDetails: 'Your details',
  stepConfirm: 'Confirmation',
  prevDay: 'Previous day',
  nextDay: 'Next day',
  today: 'Today',
  tomorrow: 'Tomorrow',
  guests: 'Guests',
  guestsSingular: 'Guest',
  pickTime: 'Pick a time',
  noSlots: 'No times left today.',
  closedToday: 'Closed on this day.',
  notConfigured: 'Opening hours not yet set up.',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  notes: 'Notes',
  notesHint: 'e.g. allergies, high chair, birthday',
  required: 'Required',
  continue: 'Continue',
  back: 'Back',
  submit: 'Reserve',
  submitting: 'Sending…',
  confirmedTitle: 'Reservation confirmed!',
  pendingTitle: 'Request sent!',
  reservedFor: 'Reserved for',
  minutes: 'minutes',
  until: 'until',
  pendingHint: 'We will confirm your request as soon as possible.',
  errInvalidEmail: 'Please enter a valid email address.',
  errInvalidPhone: 'Please enter a valid phone number.',
  errPaused: 'We are currently not accepting reservations.',
  marketingConsent: 'Send me occasional restaurant news by email.',
  privacyConsent: 'By submitting this reservation I agree that my provided data will be stored and processed to handle the reservation ({link}).',
  privacyLink: 'privacy policy',
  privacyRequired: 'Please agree to the processing of your data to reserve.',
  waitlistSwitch: 'Join the waiting list',
  waitlistHelp: 'You will be notified as soon as a table opens.',
  onlineReservation: 'Online reservation',
  step: 'Step',
  poweredBy: 'Powered by',
  notesPlaceholder: 'Birthday, allergy, special requests…',
  phonePlaceholder: '+49 30 …',
  availFull: 'Fully booked',
  availTight: 'Many slots are tight',
  availFree: 'Plenty of slots available',
  seatsFree: '{n} free',
  seatsTight: 'only {n} free',
  seatsFull: 'Full',
  notAvailable: 'unavailable',
  capacityMeta: '{cap} seats · {slots} times',
  pausedTitle: 'No reservations possible right now',
  pausedBody: 'We are temporarily not accepting online reservations. Please call us directly or try again later.',
  adminHint: 'Please log in to the admin panel and set your opening hours.',
  pendingReviewHint: 'We will review your request and get back to you.',
  confirmSentTo: 'A confirmation was sent to {email}.',
  addToCalendar: 'Add to calendar',
  afterVisit: 'After your visit:',
  leaveReview: 'Leave a review →',
  submitFailed: 'Reservation failed',
  welcomeBack: 'Welcome back! 👋',
  welcomeBackHint: 'Lovely to have you book again.',
  popular: 'Popular',
}

const tr: WidgetStrings = {
  stepDateTime: 'Tarih ve Saat',
  stepDetails: 'Bilgileriniz',
  stepConfirm: 'Onay',
  prevDay: 'Önceki gün',
  nextDay: 'Sonraki gün',
  today: 'Bugün',
  tomorrow: 'Yarın',
  guests: 'Kişi',
  guestsSingular: 'Kişi',
  pickTime: 'Saat seçin',
  noSlots: 'Bugün boş saat kalmadı.',
  closedToday: 'Bu gün kapalı.',
  notConfigured: 'Çalışma saatleri henüz tanımlanmamış.',
  firstName: 'Ad',
  lastName: 'Soyad',
  email: 'E-posta',
  phone: 'Telefon',
  notes: 'Not',
  notesHint: 'Örn: alerji, bebek sandalyesi, doğum günü',
  required: 'Zorunlu',
  continue: 'Devam',
  back: 'Geri',
  submit: 'Rezerve et',
  submitting: 'Gönderiliyor…',
  confirmedTitle: 'Rezervasyon onaylandı!',
  pendingTitle: 'Talep gönderildi!',
  reservedFor: 'Süre',
  minutes: 'dakika',
  until: 'kadar',
  pendingHint: 'Talebinizi en kısa sürede onaylayacağız.',
  errInvalidEmail: 'Geçerli bir e-posta adresi girin.',
  errInvalidPhone: 'Geçerli bir telefon numarası girin.',
  errPaused: 'Şu anda rezervasyon kabul etmiyoruz.',
  marketingConsent: 'Restoran haberlerini ara sıra e-postayla almak istiyorum.',
  privacyConsent: 'Rezervasyonu göndererek, verdiğim bilgilerin rezervasyonun işlenmesi amacıyla saklanmasını ve işlenmesini kabul ediyorum ({link}).',
  privacyLink: 'Gizlilik Politikası',
  privacyRequired: 'Rezervasyon için lütfen verilerinizin işlenmesini onaylayın.',
  waitlistSwitch: 'Bekleme listesine ekle',
  waitlistHelp: 'Bir masa boşaldığında size haber vereceğiz.',
  onlineReservation: 'Online Rezervasyon',
  step: 'Adım',
  poweredBy: 'Powered by',
  notesPlaceholder: 'Doğum günü, alerji, özel istekler…',
  phonePlaceholder: '+49 30 …',
  availFull: 'Tamamen dolu',
  availTight: 'Çoğu saat dolmak üzere',
  availFree: 'Bol miktarda yer var',
  seatsFree: '{n} boş',
  seatsTight: 'sadece {n} boş',
  seatsFull: 'Dolu',
  notAvailable: 'müsait değil',
  capacityMeta: '{cap} yer · {slots} saat',
  pausedTitle: 'Şu anda rezervasyon yapılamıyor',
  pausedBody: 'Geçici olarak online rezervasyon almıyoruz. Lütfen bizi doğrudan arayın veya daha sonra tekrar deneyin.',
  adminHint: 'Lütfen yönetim paneline giriş yapıp çalışma saatlerini girin.',
  pendingReviewHint: 'Talebinizi inceleyip size geri döneceğiz.',
  confirmSentTo: '{email} adresine bir onay gönderildi.',
  addToCalendar: 'Takvime ekle',
  afterVisit: 'Ziyaretinizden sonra:',
  leaveReview: 'Değerlendirme yapın →',
  submitFailed: 'Rezervasyon başarısız',
  welcomeBack: 'Tekrar hoş geldiniz! 👋',
  welcomeBackHint: 'Yeniden rezervasyon yapmanıza sevindik.',
  popular: 'Popüler',
}

const fr: WidgetStrings = {
  stepDateTime: 'Date et heure',
  stepDetails: 'Vos coordonnées',
  stepConfirm: 'Confirmation',
  prevDay: 'Jour précédent',
  nextDay: 'Jour suivant',
  today: "Aujourd'hui",
  tomorrow: 'Demain',
  guests: 'Personnes',
  guestsSingular: 'Personne',
  pickTime: "Choisir l'heure",
  noSlots: "Plus d'horaires disponibles aujourd'hui.",
  closedToday: 'Fermé ce jour-là.',
  notConfigured: "Horaires d'ouverture pas encore définis.",
  firstName: 'Prénom',
  lastName: 'Nom',
  email: 'E-mail',
  phone: 'Téléphone',
  notes: 'Remarque',
  notesHint: 'p. ex. allergies, chaise haute, anniversaire',
  required: 'Obligatoire',
  continue: 'Continuer',
  back: 'Retour',
  submit: 'Réserver',
  submitting: 'Envoi…',
  confirmedTitle: 'Réservation confirmée !',
  pendingTitle: 'Demande envoyée !',
  reservedFor: 'Réservé pour',
  minutes: 'minutes',
  until: "jusqu'à",
  pendingHint: 'Nous confirmerons votre demande dès que possible.',
  errInvalidEmail: 'Veuillez saisir une adresse e-mail valide.',
  errInvalidPhone: 'Veuillez saisir un numéro de téléphone valide.',
  errPaused: "Nous n'acceptons pas de réservations pour le moment.",
  marketingConsent: 'Je souhaite recevoir occasionnellement des actualités du restaurant par e-mail.',
  privacyConsent: "En envoyant cette réservation, j'accepte que les données fournies soient enregistrées et traitées pour gérer la réservation ({link}).",
  privacyLink: 'politique de confidentialité',
  privacyRequired: 'Veuillez accepter le traitement de vos données pour réserver.',
  waitlistSwitch: "S'inscrire sur la liste d'attente",
  waitlistHelp: "Vous serez averti dès qu'une table se libère.",
  onlineReservation: 'Réservation en ligne',
  step: 'Étape',
  poweredBy: 'Propulsé par',
  notesPlaceholder: 'Anniversaire, allergie, demandes particulières…',
  phonePlaceholder: '+33 1 …',
  availFull: 'Complet',
  availTight: 'De nombreux créneaux sont limités',
  availFree: 'De nombreux créneaux disponibles',
  seatsFree: '{n} libres',
  seatsTight: 'plus que {n} libres',
  seatsFull: 'Complet',
  notAvailable: 'indisponible',
  capacityMeta: '{cap} places · {slots} horaires',
  pausedTitle: 'Aucune réservation possible actuellement',
  pausedBody: "Nous n'acceptons temporairement pas de réservations en ligne. Veuillez nous appeler directement ou réessayer plus tard.",
  adminHint: "Veuillez vous connecter au panneau d'administration et indiquer vos horaires d'ouverture.",
  pendingReviewHint: 'Nous examinerons votre demande et reviendrons vers vous.',
  confirmSentTo: 'Une confirmation a été envoyée à {email}.',
  addToCalendar: 'Ajouter au calendrier',
  afterVisit: 'Après votre visite :',
  leaveReview: 'Laisser un avis →',
  submitFailed: 'Échec de la réservation',
  welcomeBack: 'Bon retour ! 👋',
  welcomeBackHint: 'Ravis de vous revoir réserver.',
  popular: 'Populaire',
}

const it: WidgetStrings = {
  stepDateTime: 'Data e ora',
  stepDetails: 'I tuoi dati',
  stepConfirm: 'Conferma',
  prevDay: 'Giorno precedente',
  nextDay: 'Giorno successivo',
  today: 'Oggi',
  tomorrow: 'Domani',
  guests: 'Persone',
  guestsSingular: 'Persona',
  pickTime: "Scegli l'orario",
  noSlots: 'Nessun orario disponibile oggi.',
  closedToday: 'Chiuso in questo giorno.',
  notConfigured: 'Orari di apertura non ancora impostati.',
  firstName: 'Nome',
  lastName: 'Cognome',
  email: 'E-mail',
  phone: 'Telefono',
  notes: 'Nota',
  notesHint: 'es. allergie, seggiolone, compleanno',
  required: 'Obbligatorio',
  continue: 'Continua',
  back: 'Indietro',
  submit: 'Prenota',
  submitting: 'Invio…',
  confirmedTitle: 'Prenotazione confermata!',
  pendingTitle: 'Richiesta inviata!',
  reservedFor: 'Riservato per',
  minutes: 'minuti',
  until: 'fino alle',
  pendingHint: 'Confermeremo la tua richiesta il prima possibile.',
  errInvalidEmail: 'Inserisci un indirizzo e-mail valido.',
  errInvalidPhone: 'Inserisci un numero di telefono valido.',
  errPaused: 'Al momento non accettiamo prenotazioni.',
  marketingConsent: 'Desidero ricevere occasionalmente novità del ristorante via e-mail.',
  privacyConsent: 'Inviando la prenotazione acconsento alla memorizzazione e al trattamento dei dati forniti per gestire la prenotazione ({link}).',
  privacyLink: 'informativa sulla privacy',
  privacyRequired: 'Per prenotare, acconsenti al trattamento dei tuoi dati.',
  waitlistSwitch: "Iscriviti alla lista d'attesa",
  waitlistHelp: 'Sarai avvisato non appena si libera un tavolo.',
  onlineReservation: 'Prenotazione online',
  step: 'Passo',
  poweredBy: 'Powered by',
  notesPlaceholder: 'Compleanno, allergia, richieste particolari…',
  phonePlaceholder: '+39 02 …',
  availFull: 'Tutto esaurito',
  availTight: 'Molti orari quasi pieni',
  availFree: 'Molti orari disponibili',
  seatsFree: '{n} liberi',
  seatsTight: 'solo {n} liberi',
  seatsFull: 'Pieno',
  notAvailable: 'non disponibile',
  capacityMeta: '{cap} posti · {slots} orari',
  pausedTitle: 'Nessuna prenotazione possibile al momento',
  pausedBody: 'Temporaneamente non accettiamo prenotazioni online. Chiamaci direttamente o riprova più tardi.',
  adminHint: 'Accedi al pannello di amministrazione e inserisci gli orari di apertura.',
  pendingReviewHint: 'Esamineremo la tua richiesta e ti risponderemo.',
  confirmSentTo: 'Una conferma è stata inviata a {email}.',
  addToCalendar: 'Aggiungi al calendario',
  afterVisit: 'Dopo la tua visita:',
  leaveReview: 'Lascia una recensione →',
  submitFailed: 'Prenotazione non riuscita',
  welcomeBack: 'Bentornato! 👋',
  welcomeBackHint: 'Felici di rivederti prenotare.',
  popular: 'Popolare',
}

const STRINGS: Record<WidgetLocale, WidgetStrings> = { de, en, tr, fr, it }

export function getWidgetStrings(locale: WidgetLocale): WidgetStrings {
  return STRINGS[locale] ?? STRINGS.de
}

/**
 * Resolve the widget's start locale. SSR-safe.
 *
 * Policy (chosen by the restaurant): ALWAYS default to German — Verde
 * and the German market expect the widget to open in German, and the
 * owner found a browser-language auto-switch confusing ('warum ist
 * mein Widget türkisch?'). Guests who prefer another language tap the
 * DE/EN/TR switcher. An explicit `?lang=de|en|tr` link/QR still wins
 * so a campaign or a localized QR can preset the language on purpose.
 *
 * navigator.language is intentionally NOT consulted anymore.
 */
export function detectWidgetLocale(): WidgetLocale {
  if (typeof window === 'undefined') return 'de'
  try {
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('lang')?.toLowerCase()
    if (fromUrl === 'de' || fromUrl === 'en' || fromUrl === 'tr' || fromUrl === 'fr' || fromUrl === 'it') return fromUrl
  } catch { /* ignore */ }
  return 'de'
}
